import { useCallback, useEffect, useState } from "react";

import { Outlet, useNavigate } from "react-router-dom";

import { useSessionStore } from "@/entities/session";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileDock } from "@/widgets/MobileDock/MobileDock";
import { SideBar } from "@/widgets/SideBar/ui/SideBar";
import { NotificationLayout } from "@/shared/ui/layout/NotificationLayout.tsx";
import { ThemeLayout } from "@/shared/ui/layout/ThemeLayout.tsx";
import { AuthLayout } from "@/shared/ui/layout/AuthLayout.tsx";
import { PreloaderLayout } from "./PreloaderLayout";

const Layout = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isRemember, logout } = useSessionStore();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const isMobile = useIsMobile({ breakpoint: 1024 });

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      logout();
      navigate("/login");
    }
  }, [isAuthenticated, navigate, logout]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "s") {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [toggleSidebar]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isRemember) {
        // Чтобы сработал диалог подтверждения
        event.preventDefault();
        event.returnValue = ""; // нужно обязательно присвоить хоть что-то
      }
    };

    const handleUnload = () => {
      if (!isRemember) {
        logout();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [isRemember, logout]);

  return (
    <AuthLayout>
      <ThemeLayout>
        <PreloaderLayout>
          <SideBar isOpen={isSidebarOpen} />

          <div
            className={`flex-1 overflow-auto bg-bgGrayColor transition-all duration-300 lg:p-6 ${
              isSidebarOpen ? "lg:ml-[270px]" : "md:ml-0"
            } h-[100vh]`}
          >
            <div
              className={
                "h-full w-full overflow-auto rounded-xl bg-bgColor max-lg:pb-14"
              }
            >
              <div className="flex h-full w-full flex-col gap-4 p-4">
                <Outlet />
              </div>
              <NotificationLayout />
            </div>
          </div>
          {isMobile && <MobileDock />}
        </PreloaderLayout>
      </ThemeLayout>
    </AuthLayout>
  );
};

export default Layout;
